// src/features/employeeApplicationUsage/components/EmployeeApplicationUsage/UsageTable.tsx

import {
  EmployeeUsage,
} from '../../types/EmployeeApplicationUsagePage/employeeApplicationUsage.types';

type Props = {
  rows: EmployeeUsage[];
};

const getCellColor = (
  value: string,
) => {
  if (
    !value ||
    value === '00:00:00'
  ) {
    return 'bg-[#F2F4F7] text-[#98A2B3]';
  }

  const [hours, minutes] =
    value.split(':').map(Number);

  const totalMinutes =
    hours * 60 + minutes;

  if (totalMinutes >= 90) {
    return 'bg-[#DCFCE7] text-[#15803D]';
  }

  if (totalMinutes >= 45) {
    return 'bg-[#DBEAFE] text-[#2563EB]';
  }

  return 'bg-[#FEF3C7] text-[#D97706]';
};

const UsageTable = ({
  rows,
}: Props) => {
  return (
    <div
      className="
        overflow-hidden
        rounded-xl
        border
        border-[#E4E7EC]
        bg-white
        shadow-sm
      "
    >
      <div className="overflow-x-auto">
        <table className="min-w-[1600px] w-full">
          <thead>
            <tr className="bg-[#002B5B] text-white">

              {/* Emp ID */}
              <th
                className="
                  sticky left-0 z-30
                  min-w-[120px]
                  bg-[#002B5B]
                  px-4 py-3
                  text-left
                  text-[11px]
                  font-semibold
                  uppercase
                "
              >
                Emp ID
              </th>

              {/* Name */}
              <th
                className="
                  sticky left-[120px] z-30
                  min-w-[180px]
                  bg-[#002B5B]
                  px-4 py-3
                  text-left
                  text-[11px]
                  font-semibold
                  uppercase
                "
              >
                Name
              </th>

              {/* Department */}
              <th
                className="
                  sticky left-[300px] z-30
                  min-w-[180px]
                  bg-[#002B5B]
                  px-4 py-3
                  text-left
                  text-[11px]
                  font-semibold
                  uppercase
                "
              >
                Department
              </th>

              {/* Total Hours */}
              <th
                className="
                  sticky left-[480px] z-30
                  min-w-[120px]
                  bg-[#002B5B]
                  px-4 py-3
                  text-center
                  text-[11px]
                  font-semibold
                  uppercase
                "
              >
                Total Hrs
              </th>

              {/* Working Hours */}
              <th
                className="
                  sticky left-[600px] z-30
                  min-w-[120px]
                  bg-[#002B5B]
                  px-4 py-3
                  text-center
                  text-[11px]
                  font-semibold
                  uppercase
                "
              >
                Working Hrs
              </th>

              {/* Productive Hours */}
              <th
                className="
                  sticky left-[720px] z-30
                  min-w-[120px]
                  bg-[#002B5B]
                  px-4 py-3
                  text-center
                  text-[11px]
                  font-semibold
                  uppercase
                "
              >
                Productive Hrs
              </th>

              {/* Scrollable Columns */}
              <th className="min-w-[140px] px-4 py-3 text-center text-[11px] font-semibold">
                Outlook
              </th>

              <th className="min-w-[140px] px-4 py-3 text-center text-[11px] font-semibold">
                Teams
              </th>

              <th className="min-w-[140px] px-4 py-3 text-center text-[11px] font-semibold">
                Edge
              </th>
            </tr>
          </thead>

          <tbody>
            {rows.map(
              (
                row,
                index,
              ) => (
                <tr
                  key={row.empId}
                  className={`
                    border-b
                    border-[#EAECF0]
                    hover:bg-[#F9FAFB]
                    ${
                      index % 2 === 0
                        ? 'bg-white'
                        : 'bg-[#FCFCFD]'
                    }
                  `}
                >
                  {/* Emp ID */}
                  <td
                    className="
                      sticky left-0 z-20
                      min-w-[120px]
                      bg-inherit
                      px-4 py-3
                      text-[12px]
                      font-semibold
                      text-[#002B5B]
                    "
                  >
                    {row.empId}
                  </td>

                  {/* Name */}
                  <td
                    className="
                      sticky left-[120px] z-20
                      min-w-[180px]
                      bg-inherit
                      px-4 py-3
                      text-[12px]
                      font-medium
                      text-[#101828]
                    "
                  >
                    {row.name}
                  </td>

                  {/* Department */}
                  <td
                    className="
                      sticky left-[300px] z-20
                      min-w-[180px]
                      bg-inherit
                      px-4 py-3
                      text-[12px]
                      text-[#667085]
                    "
                  >
                    {row.department}
                  </td>

                  {/* Total Hours */}
                  <td
                    className="
                      sticky left-[480px] z-20
                      min-w-[120px]
                      bg-inherit
                      px-4 py-3
                      text-center
                      text-[12px]
                      font-medium
                    "
                  >
                    {row.totalHours}
                  </td>

                  {/* Working Hours */}
                  <td
                    className="
                      sticky left-[600px] z-20
                      min-w-[120px]
                      bg-inherit
                      px-4 py-3
                      text-center
                      text-[12px]
                      font-medium
                      text-[#7C3AED]
                    "
                  >
                    {row.workingHours}
                  </td>

                  {/* Productive Hours */}
                  <td
                    className="
                      sticky left-[720px] z-20
                      min-w-[120px]
                      bg-inherit
                      px-4 py-3
                      text-center
                      text-[12px]
                      font-semibold
                      text-[#16A34A]
                    "
                  >
                    {row.productiveHours}
                  </td>

                  {/* Outlook */}
                  <td className="px-3 py-3 text-center">
                    <span
                      className={`
                        inline-flex
                        rounded-md
                        px-2.5
                        py-1
                        text-[11px]
                        font-medium
                        ${getCellColor(
                          row.outlook,
                        )}
                      `}
                    >
                      {row.outlook}
                    </span>
                  </td>

                  {/* Teams */}
                  <td className="px-3 py-3 text-center">
                    <span
                      className={`
                        inline-flex
                        rounded-md
                        px-2.5
                        py-1
                        text-[11px]
                        font-medium
                        ${getCellColor(
                          row.teams,
                        )}
                      `}
                    >
                      {row.teams}
                    </span>
                  </td>

                  {/* Edge */}
                  <td className="px-3 py-3 text-center">
                    <span
                      className={`
                        inline-flex
                        rounded-md
                        px-2.5
                        py-1
                        text-[11px]
                        font-medium
                        ${getCellColor(
                          row.edge,
                        )}
                      `}
                    >
                      {row.edge}
                    </span>
                  </td>
                </tr>
              ),
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default UsageTable;